import { useCallback, useEffect, useState } from "react";
import {
  getCultivationCapabilities,
  type CultivationCapabilities,
  type CultivationCapabilitiesResponse,
  type CultivationCapability,
} from "@/lib/api";

export const CAPABILITY_RESOURCES: Record<CultivationCapability, string> = {
  view: "cultivation.view",
  import: "cultivation.import",
  review: "cultivation.review",
  approve: "cultivation.approve",
  post: "cultivation.post",
  lock: "cultivation.lock",
  correct: "cultivation.correct",
  configure: "cultivation.configure",
};

export function capabilityResource(capability: CultivationCapability): string {
  return CAPABILITY_RESOURCES[capability];
}

/**
 * UI gating only mirrors the server's effective capability set. The server
 * applies the documented one-release journal fallback for every non-view close
 * permission and reports that same result here, so the returned booleans are
 * used verbatim. A null set means "not loaded yet", never a denial.
 */
export function capabilityAllowed(
  capabilities: CultivationCapabilities | null | undefined,
  capability: CultivationCapability,
): boolean {
  return capabilities ? capabilities[capability] : true;
}

export interface CultivationCapabilitiesState {
  capabilities: CultivationCapabilities | null;
  response: CultivationCapabilitiesResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

interface CapabilityRequestState {
  key: string;
  status: "idle" | "loading" | "ready" | "error";
  response: CultivationCapabilitiesResponse | null;
  error: string | null;
}

export function useCultivationCapabilities(
  legalEntityId: string | null | undefined,
): CultivationCapabilitiesState {
  const key = legalEntityId ?? "";
  const [request, setRequest] = useState<CapabilityRequestState>({
    key: "",
    status: "idle",
    response: null,
    error: null,
  });
  const [version, setVersion] = useState(0);
  const active = key !== "" && request.key === key ? request : null;
  const loading = key !== "" && (!active || active.status === "loading");

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    setRequest({ key, status: "loading", response: null, error: null });
    getCultivationCapabilities({ legal_entity_id: key })
      .then((result) => {
        if (!cancelled) {
          setRequest({ key, status: "ready", response: result, error: null });
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setRequest({
            key,
            status: "error",
            response: null,
            error: cause instanceof Error ? cause.message : String(cause),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [key, version]);

  const reload = useCallback(() => setVersion((current) => current + 1), []);

  return {
    capabilities: key ? active?.response?.capabilities ?? null : null,
    response: key ? active?.response ?? null : null,
    loading,
    error: key ? active?.error ?? null : null,
    reload,
  };
}
